import { v4 as uuid } from 'uuid';

import { actionTypes } from '../actions/types';
import ApplicationStatus from '../util/mappings/applicationStatus';
import SigningStatus from '../util/mappings/signingStatus';

type AppActions = import('../actions/types').AppActions;

export interface Step {
    id: string;
    open: boolean;
    available: boolean;
    done: boolean;
    description?: string;
    hideNextButton?: boolean;
    onNextClick?: undefined;
}

export interface StepsReducer {
    steps: Step[];
}

const INSURANCE_STEP = 'LoanCompletionInsurance';

const insuranceStep: Step = {
    id: uuid(),
    open: false,
    available: true,
    done: false,
    description: INSURANCE_STEP,
};

export const initialState: StepsReducer = {
    steps: [
        {
            id: uuid(),
            open: true,
            available: true,
            done: false,
            description: 'LoanCompletionStart',
        },
        {
            id: uuid(),
            open: false,
            available: true,
            done: false,
            description: 'LoanCompletionSbl',
        },
        {
            id: uuid(),
            open: false,
            available: true,
            done: false,
            description: 'LoanCompletionDetails',
        },
        {
            id: uuid(),
            open: false,
            available: true,
            done: false,
            description: 'LoanCompletionKyc',
        },
        {
            id: uuid(),
            open: false,
            available: true,
            done: false,
            description: 'LoanCompletionSigning',
        },
        {
            id: uuid(),
            open: false,
            available: true,
            done: false,
            description: 'LoanCompletionReceipt',
        },
    ],
};

export const stepsReducer = (state = initialState, action: AppActions = {} as AppActions): StepsReducer => {
    switch (action.type) {
        case actionTypes.OPEN_STEP: {
            const destinationStep = action.payload;
            const destinationStepIndex = destinationStep - 1;
            return {
                ...state,
                steps: state.steps.map((step, i) => ({
                    ...step,
                    open: destinationStepIndex === i,
                    done: destinationStepIndex < i ? false : step.done,
                })),
            };
        }
        case actionTypes.UPDATE_STEPS: {
            const steps = action.payload;
            return {
                ...state,
                steps: [
                    {
                        ...state.steps[0],
                        ...steps[0],
                    },
                    {
                        ...state.steps[1],
                        ...steps[1],
                    },
                    {
                        ...state.steps[2],
                        ...steps[2],
                    },
                    {
                        ...state.steps[3],
                        ...steps[3],
                    },
                    {
                        ...state.steps[4],
                        ...steps[4],
                    },
                    {
                        ...state.steps[5],
                        ...steps[5],
                    },
                    {
                        ...state.steps[6],
                        ...steps[6],
                    },
                ],
            };
        }

        case actionTypes.SEND_APPLICATION_FULFILLED:
        case actionTypes.FETCH_APPLICATION_FULFILLED:
        case actionTypes.UPDATE_APPLICATION_FULFILLED: {
            const applicationStatus = action.payload?.applicationStatus;
            const esigningStatus = action.payload?.esigningStatus || '';
            const signedByCurrentUser = action.payload?.signedByCurrentUser;
            const missingVariables = action.payload?.missingVariables || [];

            if (
                [SigningStatus.ongoingSigning, SigningStatus.notSigned].includes(esigningStatus) &&
                !signedByCurrentUser &&
                missingVariables.length === 0
            ) {
                return {
                    ...state,
                    steps: [
                        {
                            ...state.steps[0],
                            open: false,
                        },
                        {
                            ...state.steps[1],
                            open: false,
                        },
                        {
                            ...state.steps[2],
                            open: false,
                        },
                        {
                            ...state.steps[3],
                            open: false,
                        },
                        {
                            ...state.steps[4],
                            open: false,
                        },
                        {
                            ...state.steps[5],
                            open: true,
                        },
                        {
                            ...state.steps[6],
                            open: false,
                        },
                    ],
                };
            }

            if (
                applicationStatus === ApplicationStatus.documentsComplete ||
                (applicationStatus === ApplicationStatus.documentsSent && signedByCurrentUser)
            ) {
                return {
                    ...state,
                    steps: [
                        {
                            ...state.steps[0],
                            open: false,
                        },
                        {
                            ...state.steps[1],
                            open: false,
                        },
                        {
                            ...state.steps[2],
                            open: false,
                        },
                        {
                            ...state.steps[3],
                            open: false,
                        },
                        {
                            ...state.steps[4],
                            open: false,
                        },
                        {
                            ...state.steps[5],
                            open: false,
                        },
                        {
                            ...state.steps[6],
                            open: true,
                        },
                    ],
                };
            }
            return state;
        }
        case actionTypes.UPDATE_STEPS_COMPLETION: {
            const steps = [...state.steps];
            const alreadyExisted = steps.find((step: Step) => {
                return step.description === INSURANCE_STEP;
            });
            if (alreadyExisted) {
                return state;
            }
            steps.splice(2, 0, insuranceStep);
            return {
                ...state,
                steps,
            };
        }

        default:
            return state;
    }
};
